import {CloseIcon, mergeRefs, SearchIcon} from "@hosttools/frontend";
import {Input, InputField, Pressable} from "@hosttools/frontend/components";
import {
    forwardRef,
    type ForwardRefRenderFunction,
    memo,
    useCallback,
    useEffect,
    useRef
} from "react";

type Props = {
    query: string;
    testID?: string;
    placeholder?: string;
    onChangeText: (text: string) => void;
    onClear: () => void;
    onSearch?: (text: string) => void;
    onBlur?: () => void;
};

const SearchInput: ForwardRefRenderFunction<HTMLInputElement, Props> = (
    {query, testID, placeholder = "Search", onSearch, onChangeText, onClear, onBlur},
    inputRef
) => {
    const innerRef = useRef<HTMLInputElement>(null);

    const handleKeyPress = useCallback(
        (event: KeyboardEvent) => {
            event.stopPropagation();
            if (event.key === "Enter") {
                event.preventDefault();
                onSearch?.(query);
            }
        },
        [query, onSearch]
    );

    const onClearInput = useCallback(() => {
        onClear();
        innerRef.current?.focus();
    }, [onClear]);

    const handleFocus = useCallback(() => {
        const inputEl = innerRef.current;
        inputEl?.setSelectionRange(query.length, query.length);
    }, [query]);

    useEffect(() => {
        const inputEl = innerRef.current;
        inputEl?.addEventListener("keydown", handleKeyPress);
        return () => {
            inputEl?.removeEventListener("keydown", handleKeyPress);
        };
    }, [handleKeyPress]);

    return (
        // adding `form` helps to avoid the autofill somehow
        // setting `autoComplete="off"` doesn't work somehow
        <form action="#" role="search">
            <Input
                leftElement={<SearchIcon color="blueGray.400" />}
                rightElement={
                    query ? (
                        <Pressable onPress={onClearInput}>
                            <CloseIcon color="blueGray.400" size="xs" />
                        </Pressable>
                    ) : undefined
                }
                variant="outline"
            >
                <InputField<"web">
                    ref={mergeRefs(innerRef, ...(inputRef ? [inputRef] : []))}
                    autoComplete="off"
                    placeholder={placeholder}
                    testID={testID}
                    value={query}
                    onBlur={onBlur}
                    onChangeText={onChangeText}
                    onFocus={handleFocus}
                />
            </Input>
        </form>
    );
};

export default memo(forwardRef<HTMLInputElement, Props>(SearchInput));
